import { Action, Reducer } from 'redux';
import { AppThunkAction } from '.';
import { toast } from 'react-toastify';
import { CheckoutTypes } from '../models/CheckoutTypes';
import { ImpactPartner } from '../models/ImpactPartner';
import { ShoppingCartItem } from "../models/ShoppingCart";
import CheckoutService from '../services/CheckoutService';
import * as AuthorizationStore from '../store/Authorization';
import * as Analytics from '../AnalyticsHelper';
import { CheckoutCommonField } from '../models/ReceivedOrder';
import { validateCheckoutCommonFields } from '../pages/Checkout/CheckoutUtils';
import { ImpactPartnerResultDto } from '../models/OrderResponseDto';

export interface IState {
    items: ShoppingCartItem[];
    checkoutCommonFields: CheckoutCommonField[];
    selectedFiles: any;
    selectedFile: any;
    info: {
        currencyCode: string;
        items: ShoppingCartItem[];
    };
    customerOrderNumber: string;
    partnerResults: ImpactPartnerResultDto[];
    orderSubmitStatusCode?: number;
    impactPartnerProcessed: boolean;
    hasErrorOnSubmit: boolean;
    orderSubmitErrorMessage?: string;
    refundId: string;
    isSubmittingOrder: boolean;
    isFieldFileAttaching: boolean;
    isFetchingCheckoutCommonFields: boolean;
    checkoutCommonFieldsLoaded: boolean;
    errorLoadedCheckoutCommonFields: boolean;
    error: any;
    currentType: CheckoutTypes;
    cartProductsChanged: boolean;
    cartProductsVerified: boolean;
}

const unloadedPatnerState: IState = {
    items: [],
    checkoutCommonFields: undefined,
    selectedFiles: [],
    selectedFile: undefined,
    info: {
        currencyCode: "",
        items: []
    },
    customerOrderNumber: undefined,
    partnerResults: [],
    orderSubmitStatusCode: undefined,
    impactPartnerProcessed: false,
    hasErrorOnSubmit: false,
    orderSubmitErrorMessage: "",
    refundId: "",
    isSubmittingOrder: false,
    isFieldFileAttaching: false,
    isFetchingCheckoutCommonFields: false,
    checkoutCommonFieldsLoaded: false,
    errorLoadedCheckoutCommonFields: false,
    error: undefined,
    currentType: CheckoutTypes.REVIEW,
    cartProductsChanged: false,
    cartProductsVerified: false,
};

export enum Actions {
    SetCartItems = "SetCartItems",
    BeginLoadCheckoutCommonFields = "BeginLoadCheckoutCommonFields",
    LoadCheckoutCommonFields = "LoadCheckoutCommonFields",
    GetUserCarts = "GetUserCarts",
    MoveToInfoStep = "MoveToInfoStep",
    MoveToReviewStep = "MoveToReviewStep",
    InfoChanged = "InfoChanged",
    PartnerCheckoutResetState = "PartnerCheckoutResetState",
    PartnerCheckoutResetCurrentStepState = "PartnerCheckoutResetCurrentStepState",
    OrderSubmit = "OrderSubmit",
    VerifyOrderProducts = "VerifyOrderProducts",
}

interface SetCartItemsAction {
    type: Actions.SetCartItems;
    userId: string;
    cartItems: ShoppingCartItem[];
}

interface BeginLoadCheckoutCommonFieldsAction {
    type: Actions.BeginLoadCheckoutCommonFields;
}

interface LoadCheckoutCommonFieldsAction {
    type: Actions.LoadCheckoutCommonFields;
    checkoutCommonFields: CheckoutCommonField[];
    hasErrorsToLoad: boolean;
}

interface InfoChangedAction {
    type: Actions.InfoChanged;
    info: any;
}

interface PartnerCheckoutResetStatedAction {
    type: Actions.PartnerCheckoutResetState;
}

interface PartnerCheckoutResetCurrentStepStateAction {
    type: Actions.PartnerCheckoutResetCurrentStepState;
}

interface OrderSubmitAction {
    type: Actions.OrderSubmit;
    currentType: CheckoutTypes;
    customerOrderNumber: string;
    impactPartnerProcessed?: boolean;
    hasError: boolean;
    statusCode?: number;
    errorMessage?: string;
}

interface VerifyOrderProductsAction {
    type: Actions.VerifyOrderProducts;
    cartProductsChanged: boolean;
    cartProductsVerified: boolean;
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction = SetCartItemsAction | LoadCheckoutCommonFieldsAction
    | InfoChangedAction | BeginLoadCheckoutCommonFieldsAction | PartnerCheckoutResetStatedAction
    | OrderSubmitAction | VerifyOrderProductsAction | PartnerCheckoutResetCurrentStepStateAction;

export const actionCreators = {
    setCartItems: (cartItems: ShoppingCartItem[]): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();

        if (state && state.authorization && state.authorization.currentUser) {
            dispatch({
                type: Actions.SetCartItems,
                userId: state.authorization.currentUser.UserId,
                cartItems: cartItems
            });
        }
    },

    loadCheckoutCommonFields: (cartId:string, partnerId: string): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();

        if (state && state.authorization && state.authorization.currentUser) {

            dispatch({
                type: Actions.BeginLoadCheckoutCommonFields,
            });

            const response = await CheckoutService.getOrderCheckoutCommonFields(cartId, partnerId);

            const hasErrors = response.statusCode !== 200 || response.hasErrors;

            let checkoutCommonFields: CheckoutCommonField[] = null;
            if (!hasErrors) {
                checkoutCommonFields = validateCheckoutCommonFields(response.value);
            }
            dispatch({
                type: Actions.LoadCheckoutCommonFields,
                checkoutCommonFields: checkoutCommonFields,
                hasErrorsToLoad: hasErrors
            });
        }
    },

    infoChanged: (info): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();

        if (state && state.authorization && state.authorization.currentUser) {
            dispatch({
                type: Actions.InfoChanged,
                info
            });
        }
    },

    resetState: (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();

        if (state && state.authorization && state.authorization.currentUser) {
            dispatch({
                type: Actions.PartnerCheckoutResetState
            });
        }
    },

    resetCurrentStepState: (): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();

        if (state && state.authorization && state.authorization.currentUser) {
            dispatch({
                type: Actions.PartnerCheckoutResetCurrentStepState
            });
        }
    },

    orderSubmit: (cartId: string, order: any, orderPartner: ImpactPartner): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();

        if (state && state.authorization && state.authorization.currentUser) {
            const partner = AuthorizationStore.getImpactPartnerFromLocalStorage();
            const response = await CheckoutService.submitOrder(cartId, order, orderPartner, state.authorization.currentUser.UserId, partner.Id);

            if (!response.hasErrors) {
                dispatch({
                    type: Actions.OrderSubmit,
                    currentType: CheckoutTypes.ORDERSUBMITTED,
                    customerOrderNumber: response.value?.customerOrderNumber ?? "",
                    hasError: false,
                    impactPartnerProcessed: true,
                    statusCode: response.statusCode
                });

                Analytics.push({
                    'event': 'inet-order',
                    'partner-name': orderPartner.Name
                });
            }
            else {
                toast.error("Order submission failed!");
                console.log("OrderSubmit", response.errorMessage);

                if (response.statusCode === 409) {
                    dispatch({
                        type: Actions.VerifyOrderProducts,
                        cartProductsChanged: true,
                        cartProductsVerified: false
                    });
                }
                else {
                    dispatch({
                        type: Actions.OrderSubmit,
                        currentType: CheckoutTypes.ORDERSUBMITTED,
                        customerOrderNumber: undefined,
                        hasError: true,
                        statusCode: response.statusCode,
                        errorMessage: response.errorMessage
                    });
                }
            }
        }
    },

    checkOrderProducts: (cartId: string, partner: ImpactPartner): AppThunkAction<KnownAction> => async (dispatch, getState) => {
        const state = getState();
        if (state && state.authorization && state.authorization.currentUser) {

            const response = await CheckoutService.checkOrderProducts(cartId, partner);

            if (!response.hasErrors) {
                dispatch({
                    type: Actions.VerifyOrderProducts,
                    cartProductsChanged: response.value.isChanged,
                    cartProductsVerified: true
                });
            }
        }
    }
}

export const reducer: Reducer<IState> = (state: IState | undefined, incomingAction: Action): IState => {

    if (state === undefined) {
        return unloadedPatnerState;
    }

    var info = Object.assign({}, state.info);

    const action = incomingAction as KnownAction;

    switch (action.type) {
        case Actions.SetCartItems: {
            return {
                ...state,
                items: action.cartItems
            };
        }

        case Actions.BeginLoadCheckoutCommonFields: {
            return {
                ...state,
                isFetchingCheckoutCommonFields: true
            };
        }

        case Actions.LoadCheckoutCommonFields: {
            return {
                ...state,
                checkoutCommonFields: action.checkoutCommonFields,
                isFetchingCheckoutCommonFields: false,
                checkoutCommonFieldsLoaded: true,
                errorLoadedCheckoutCommonFields: action.hasErrorsToLoad
            };
        }

        case Actions.OrderSubmit: {
            return {
                ...state,
                currentType: action.currentType,
                customerOrderNumber: action.customerOrderNumber,
                hasErrorOnSubmit: action.hasError,
                isSubmittingOrder: false,
                impactPartnerProcessed: action.impactPartnerProcessed,
                orderSubmitStatusCode: action.statusCode,
                orderSubmitErrorMessage: action.errorMessage,
            };
        }

        case Actions.VerifyOrderProducts: {

            return {
                ...state,
                cartProductsChanged: action.cartProductsChanged,
                cartProductsVerified: true,
                isSubmittingOrder: true,
                hasErrorOnSubmit: false,
                impactPartnerProcessed: false
            };
        }

        case Actions.InfoChanged: {
            return {
                ...state,
                info: action.info
            };
        }

        case Actions.PartnerCheckoutResetState: {
            unloadedPatnerState.info = {
                currencyCode: "",
                items: []
            };

            return unloadedPatnerState;
        }

        case Actions.PartnerCheckoutResetCurrentStepState: {
            unloadedPatnerState.info = {
                currencyCode: "",
                items: []
            };

            unloadedPatnerState.partnerResults = state.partnerResults;

            return unloadedPatnerState;
        }

        default: return state;
    }
}